import { z } from 'zod';
import { TagSchema, tagKey, type Tag, type TagColour } from '../../domain/entities/tag.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged } from '../../ports/batch.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Unsubscribe } from '../../ports/query.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * Managing a company's tags.
 *
 * Small, and the only interesting part is that a duplicate is refused by
 * name rather than by id: "Weather" and "weather " are the same word to
 * everybody but a database, and a tag list holding both is a tag list
 * nobody trusts — and a filter by one of them silently misses half the
 * records.
 */
export class TagError extends Error {
  readonly reason: 'invalid' | 'duplicate' | 'inUse';

  constructor(reason: 'invalid' | 'duplicate' | 'inUse', message: string) {
    super(message);
    this.name = 'TagError';
    this.reason = reason;
  }
}

export const SaveTagInputSchema = z.object({
  name: z.string().trim().min(1, 'A tag needs a name').max(60),
  colour: z.custom<TagColour>().optional(),
});
export type SaveTagInput = z.input<typeof SaveTagInputSchema>;

export interface TagContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly uid: string;
}

function repo(context: Pick<TagContext, 'data' | 'companyId'>) {
  return context.data.collection<Tag>({ companyId: context.companyId }, 'tags');
}

export async function listTags(context: Pick<TagContext, 'data' | 'companyId'>): Promise<Tag[]> {
  const page = await repo(context).list({ orderBy: [{ field: 'name', direction: 'asc' }] });
  return [...page.items];
}

export function subscribeToTags(
  context: Pick<TagContext, 'data' | 'companyId'>,
  onChange: (tags: readonly Tag[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return repo(context).subscribe(
    { orderBy: [{ field: 'name', direction: 'asc' }] },
    (page) => onChange(page.items),
    onError,
  );
}

export async function createTag(context: TagContext, input: SaveTagInput): Promise<Tag> {
  const parsed = SaveTagInputSchema.safeParse(input);
  if (!parsed.success) throw new TagError('invalid', parsed.error.issues[0]!.message);

  const existing = await listTags(context);
  const clash = existing.find((tag) => tagKey(tag.name) === tagKey(parsed.data.name));
  if (clash) throw new TagError('duplicate', `“${clash.name}” already exists`);

  const now = context.clock.now().toISOString();
  const tag: Tag = {
    id: newId(),
    companyId: context.companyId,
    name: parsed.data.name,
    colour: parsed.data.colour ?? 'slate',
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };

  const checked = TagSchema.safeParse(tag);
  if (!checked.success) throw new TagError('invalid', checked.error.issues[0]!.message);

  await repo(context).create(tag);
  return tag;
}

export async function renameTag(
  context: TagContext,
  id: string,
  input: SaveTagInput,
): Promise<Tag> {
  const parsed = SaveTagInputSchema.safeParse(input);
  if (!parsed.success) throw new TagError('invalid', parsed.error.issues[0]!.message);

  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('tags', id);

  const others = (await listTags(context)).filter((tag) => tag.id !== id);
  const clash = others.find((tag) => tagKey(tag.name) === tagKey(parsed.data.name));
  if (clash) throw new TagError('duplicate', `“${clash.name}” already exists`);

  const patch = {
    name: parsed.data.name,
    colour: parsed.data.colour ?? existing.colour,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };
  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId },
      collection: 'tags',
      id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return { ...existing, ...patch };
}

/**
 * Delete a tag.
 *
 * The records that used it keep the id, and `tagNames` skips an id with no
 * tag behind it — so a deleted tag disappears from the records rather than
 * leaving a ULID on screen. Sweeping every record to remove the id would be
 * a job-by-job pass over four collections for a tidying action, and the
 * leftover id is invisible.
 */
export async function deleteTag(context: TagContext, id: string): Promise<void> {
  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('tags', id);
  await repo(context).delete(id);
}

/** Add or remove a tag from a selection of ids, for a picker. */
export function toggleTag(ids: readonly string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((other) => other !== id) : [...ids, id];
}
